import React, { useState, useEffect, useRef } from 'react';
import AsyncSelect from 'react-select/async';
import './EmployeeCreation.css';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { buildDiffPreview, fireAudit } from '../utils/auditClient';

const EmployeeCreation = () => {
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const employeeId = searchParams.get("employeeId");
  const navigate = useNavigate();

  const [employee, setEmployee] = useState({
    name: '',
    email: '',
    mobile: '',
    department: '',
    role: '',
    userId: null,
    id: null,
  });

  const baselineRef = useRef(null); 
  const [searchInput, setSearchInput] = useState('');

  const buildSubmitPayload = (eObj = employee) => ({
    id: eObj.id || null,
    name: eObj.name || '',
    email: eObj.email || '',
    mobileNumber: eObj.mobile || '',
    department: eObj.department || '',
    role: eObj.role || '',
    previousUserId: eObj.userId || null,
  });

  const loadEmailOptions = async (inputValue) => {
    if (!inputValue) return [];
    setLoading(true);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_SEARCH_EMPLOYEE}?email=${encodeURIComponent(inputValue)}`,
        {
          method: 'GET',
          headers: {
            'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
          },
        }
      );
      const data = await res.json();
      if (data.success && Array.isArray(data.data?.userList) && data.data.userList.length > 0) {
        const options = data.data.userList.map((user) => ({
          value: user.email,
          label: user.email,
          id: user.id,
        }));
        setLoading(false);
        return options;
      }
      setLoading(false);
      return [];
    } catch (error) {
      console.error('Error fetching email options:', error);
      setLoading(false);
      return [];
    }
  };

  const handleEmailSelect = async (option) => {
    if (!option) {
      setEmployee((emp) => ({
        ...emp,
        name: '',
        email: '',
        mobile: '',
        userId: null,
      }));
      return;
    }

    const selectedEmail = option.value;
    setEmployee((emp) => ({
      ...emp,
      email: selectedEmail,
      name: selectedEmail.split('@')[0].replace('.', ' '),
      userId: option.id,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEmployee((emp) => ({ ...emp, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!employee.email || !employee.userId) return;

    const payload = buildSubmitPayload(employee);
    const oldPayload = baselineRef.current || {};

    if (employee.id) {
      const { digest, html } = buildDiffPreview(oldPayload, payload);
      if (!digest.length) {
        Swal.fire({
          icon: 'info',
          title: 'Nothing to update',
          text: 'No differences detected.',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 2500,
        });
        return;
      }
      const confirm = await Swal.fire({
        title: 'Apply these changes?',
        html,
        showCancelButton: true,
        confirmButtonText: 'Update',
      });
      if (!confirm.isConfirmed) return;
    }

    setLoading(true);
    try {
      const url = process.env.REACT_APP_API_EMPLOYEE_CREATE;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (!data.success) {
        throw new Error(data.success_message || data.message || 'Something went wrong!');
      }
      if (!response.ok) {
        throw new Error(data.message || response.statusText || 'Network error!');
      }

      fireAudit({
        entity: 'employee',
        affected_id: employee.id || null,
        action: employee.id ? 'update' : 'create',
        businessApi: url,
        businessMethod: 'POST',
        oldPayload,
        newPayload: payload,
        section: 'Ticket',
        subSection: 'Employee Creation',
        extra: { page: 'EmployeeCreation' },
      });

      Swal.fire({
        icon: 'success',
        title: employee.id ? 'Employee Updated!' : 'Employee Created!',
        text: `${employee.name} has been successfully ${employee.id ? 'updated' : 'created'}.`,
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });

      baselineRef.current = payload;

      navigate(-1);

      setEmployee({
        name: '',
        email: '',
        mobile: '',
        department: '',
        role: '',
        userId: null,
        id: null,
      });
      setSearchInput('');
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: error.message || 'Failed. Please try again.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!employeeId) {
   
      baselineRef.current = buildSubmitPayload({
        name: '',
        email: '',
        mobile: '',
        department: '',
        role: '',
        userId: null,
        id: null,
      });
      return;
    }

    const fetchEmployeeDetails = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_EMPLOYEE_LIST}?id=${employeeId}`,
          {
            method: 'GET',
            headers: {
              'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
            },
          }
        );

        const data = await response.json();

        if (data.success && Array.isArray(data.data?.employees) && data.data.employees.length > 0) {
          const employeeData = data.data.employees[0];
          const next = {
            name: employeeData.name || '',
            email: employeeData.email || '',
            mobile: employeeData.mobileNumber || '',
            department: employeeData.department || '',
            role: employeeData.role || '',
            userId: employeeData.previousUserId || null,
            id: employeeData.id || null,
          };
          setEmployee(next);
          setSearchInput(next.email);


          baselineRef.current = buildSubmitPayload(next);
        }
      } catch (error) {
        console.error('Error fetching employee details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeeDetails();
  }, [employeeId]);

  return (
    <div className="enrollment-container">
      <div className="breadcrumb">
        <Link to="/employee" className="breadcrumb-link">Employee List</Link> &gt; {employee.id ? 'Update Employee' : 'New Employee'}
      </div>

      <div className="floating-select-form" style={{ width: '100%' }}>
        <AsyncSelect
          name="search-email"
          inputId="email-select"
          inputValue={searchInput}
          onInputChange={setSearchInput}
          loadOptions={loadEmailOptions}
          onChange={handleEmailSelect}
          menuPortalTarget={document.body}
          placeholder="Type email to search…"
          className={`walkin-form-dropdown ${employee.name ? 'has-value' : ''}`}
          classNamePrefix="walkin-select"
          styles={{
            control: provided => ({
              ...provided,
              padding: '5px',
            }),
            placeholder: provided => ({
              ...provided,
              color: 'black',
            }),
          }}
        />
        {employee.name && <label htmlFor="email-select">Employee Email</label>}
      </div>

      <form onSubmit={handleSubmit} className="form">
        <div className="floating-select-form">
          <input
            name="name"
            className={`walkin-form ${employee.name ? 'has-value' : ''}`}
            value={employee.name}
            onChange={handleChange}
            placeholder="Employee Name"
          />
          {employee.name && <label>Name</label>}
        </div>

        <div className="floating-select-form">
          <input
            name="mobile"
            type="text"
            inputMode="numeric"
            pattern="\d*"
            maxLength={10}
            className={`walkin-form ${employee.mobile ? 'has-value' : ''}`}
            value={employee.mobile}
            placeholder="Enter Mobile Number"
            required
            onKeyPress={e => {
              if (!/[0-9]/.test(e.key)) e.preventDefault();
            }}
            onChange={e => {
              const onlyNums = e.target.value.replace(/\D/g, '');
              setEmployee(emp => ({ ...emp, mobile: onlyNums }));
            }}
          />
          {employee.mobile && <label>Mobile</label>}
        </div>

        <div className="floating-select-form" style={{ width: '99%' }}>
          <input
            name="department"
            className={`walkin-form ${employee.department ? 'has-value' : ''}`}
            value={employee.department}
            onChange={handleChange}
            placeholder="Enter Department"
          />
          {employee.department && <label htmlFor="department-select">Department</label>}
        </div>

        <div className="floating-select-form" style={{ width: '99%' }}>
          <input
            name="role"
            className={`walkin-form ${employee.role ? 'has-value' : ''}`}
            value={employee.role}
            onChange={handleChange}
            placeholder="Enter Role"
          />
          {employee.role && <label htmlFor="role-select">Role</label>}
        </div>

        <button
          type="submit"
          className="submit-button"
          disabled={!employee.email || loading}
        >
          {loading ? 'Processing…' : employee.id ? 'Update Employee' : 'Create Employee'}
        </button>
      </form>
    </div>
  );
};

export default EmployeeCreation;
